/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
/**
 * File created by John M. Woodcock < john@userx.co >
 * on 7/22/2025
 * for userx-support
 */

import React, {useEffect, useState} from "react";
import supportAPI from "../../_apis/supportAPI";
import * as Array from "@apollo/client/utilities";

const CompanyPreferences = ({company_id, pendingPrefs, onSaved = null}) => {
    const [prefsId, setPrefsId] = useState(null);
    const [prefs, setPrefs] = useState(pendingPrefs || {
        readiness: 'quarterly',
        capabilities: ['maintenance']
    });
    const [saved, setSaved] = useState(false);

    const normalizePrefs = (raw = {}) => {
        const readiness = raw.readiness || raw.frequency || 'quarterly';
        const capabilities = Array.isArray(raw.capabilities)
            ? raw.capabilities
            : Array.isArray(raw.intensity)
                ? raw.intensity
                : ['maintenance'];

        return { readiness, capabilities };
    };

    useEffect(() => {
        supportAPI.get(`/company/${company_id}/meta/maintenance_prefs`).then(response => {
            try {
                let data = response?.data?.[0];

                if (data) {
                    setPrefsId(data.id);
                    const parsed = JSON.parse(data.meta_value);
                    setPrefs(normalizePrefs(parsed));
                }
                // No else — already using good defaults
            } catch (err) {
                console.error('Invalid JSON from meta:', err);
            }
        });
    }, [company_id]);

    const handleReadinessChange
              = (e) => {
        setPrefs(prev => ({...prev, readiness: e.target.value}));
    }

    const handleCapabilitiesChange
              = (e) => {
        setPrefs(prev => {
            const value = e.target.value;
            const isChecked = e.target.checked;
            const current = prev.capabilities;

            return {
                ...prev,
                capabilities: isChecked
                               ? [...current, value]                      // add
                               : current.filter(item => item !== value)   // remove
            };
        });
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        setSaved(false);
        if (!company_id) return;

        const data = {
            company_id,
            meta_key:   'maintenance_prefs',
            meta_value: JSON.stringify(prefs)
        };

        if (prefsId) {
            // UPDATE existing meta row
            supportAPI.put(`/company/${company_id}/meta/${prefsId}`, JSON.stringify(data)).then(() => {
                console.log('Maintenance Prefs updated');
                setSaved(true);
                if(onSaved){
                    onSaved(true);
                }
            }).catch(err => {
                console.error('Failed to update prefs:', err);
            });
        } else {
            // CREATE new meta row
            supportAPI.post(`/company/${company_id}/meta`, JSON.stringify(data)).then(response => {
                console.log('Maintenance Prefs saved');
                // optionally update prefsId with the new ID returned
                if (response?.data?.id) {
                    setPrefsId(response.data.id);
                    if(onSaved){
                        onSaved(true);
                    }
                }
            }).catch(err => {
                console.error('Failed to save prefs:', err);
            });
        }
    };

    const calculateEstimate = () => {
        console.log('Calculating estimate with prefs:', prefs);
        if (!prefs || !prefs.readiness || !Array.isArray(prefs.capabilities)) return null;

        const activeReadiness = prefs.readiness;
        const activeCapabilities = prefs.capabilities;

        const basePrices = {
            'monthly': 20,
            'quarterly': 35,
            'semi-annually': 50,
        };

        const capabilityMultipliers = {
            'maintenance': 1,
            'security': 3,
            'performance': 2,
        };

        // 1. Calculate Cost
        let base = basePrices[activeReadiness] || 0;
        let multiplier = 1;

        activeCapabilities.forEach(cap => {
            if (capabilityMultipliers[cap]) {
                multiplier += (capabilityMultipliers[cap] - 1);
            }
        });

        const total = (base * multiplier).toFixed(2);
        const readinessLabel = activeReadiness === 'monthly' ? 'month' :
            activeReadiness === 'quarterly' ? 'quarter' : 'six months';


        return <span>Estimated cost: <strong>${total} per {readinessLabel}</strong></span>;
    };


    const renderSummary = () => {
        console.log('Rendering summary with prefs:', prefs);
        if (!prefs || !prefs.readiness || !Array.isArray(prefs.capabilities)) return null;

        const {readiness, capabilities} = prefs;

        const readinessLabels = {
            'monthly': 'Elite Guard',
            'quarterly': 'Routine Surveillance',
            'semi-annually': 'The Bare Minimum'
        };

        const capabilitiesLabels = {
            maintenance:     'core instability',
            security:    'a broken shield',
            performance: 'sluggish response'
        };

        const selected = capabilities.map(key => capabilitiesLabels[key] || key);

        const formatList = (items) => {
            if (items.length === 0) return '';
            if (items.length === 1) return items[0];
            if (items.length === 2) return `${items[0]} and ${items[1]}`;
            return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
        };

        const qualities = [];
        if (prefs.capabilities.includes('maintenance')) qualities.push('up-to-date');
        if (prefs.capabilities.includes('security')) qualities.push('impenetrable');
        if (prefs.capabilities.includes('performance')) qualities.push('fast');

        return (
            <>
                <p>
                    Excellent strategy! By activating <strong>{readinessLabels[readiness]}</strong> readiness, you're ensuring your mission
                    is never interupted by {selected.length > 0 ? <strong>{formatList(selected)}</strong> : 'unforseen vulnerabilities'}.
                    You focus on changing the world - we'll keep the fortress {qualities.length > 0 ? <strong>{formatList(qualities)}</strong> : 'protected'}.
                </p>

                {
                    readiness === 'semiannually' && (
                        <p className="text-danger small">
                            Semi-annual maintenance is not recommended for most sites.
                        </p>
                    )
                }
            </>
        )
            ;
    };


    return (
        <div className="wrap">
            <h3>Equip Your Headquarters</h3>
            <p>Adjust your selections below to see how our Hero Support scales with your mission.</p>
            <form onSubmit={handleSubmit}>
                <div className={`tabpanel d-flex justify-content-evenly gap-5`}>
                    <div className={'w-25'}>
                        <h4>Operational Readiness</h4>
                        <p className={'small'}>Choose how often you want your headquarters inspected.</p>
                        <label className={'form-label d-block'}>
                            <input className={'me-3'}
                                   type={'radio'}
                                   name={'readiness'}
                                   value={'monthly'}
                                   checked={prefs && prefs.readiness === 'monthly'}
                                   onChange={handleReadinessChange
                            }
                            />
                            <strong>Monthly: Elite Guard</strong>
                            <div className={'small'}>The gold standard for heroes who cannot afford a second of downtime.
                                Keep your site perpetually fresh and optimized.</div>
                        </label>
                        <label className={'form-label d-block'}>
                            <input className={'me-3'}
                                   type={'radio'}
                                   name={'readiness'}
                                   value={'quarterly'}
                                   checked={prefs && prefs.readiness === 'quarterly'}
                                   onChange={handleReadinessChange
                            }
                            />
                            <strong>Quarterly: Routine Surveillance</strong>
                            <div className={'small'}>Best for stable headquarters that need periodic strategic audits to
                                ensure everything remains optimal.</div>
                        </label>
                        <label className={'form-label d-block'}>
                            <input className={'me-3'}
                                   type={'radio'}
                                   name={'readiness'}
                                   value={'semi-annually'}
                                   checked={prefs && prefs.readiness === 'semi-annually'}
                                   onChange={handleReadinessChange
                            }
                            />
                            <strong>Semi-Annually: The Bare Minimum</strong>
                            <div className={'small'}>For simpler missions that just need foundational technical support
                                twice a year.</div>
                        </label>
                    </div>
                    <div className={'w-25'}>
                        <h4>Mission Capabilities</h4>
                        <p className={'small'}>Select the areas of vulnerability you wish to protect.</p>
                        <label className={'form-label d-block'}>
                            <input className={'me-3'}
                                   type={'checkbox'}
                                   name={'capabilities'}
                                   value={'maintenance'}
                                   checked={prefs && prefs.capabilities && prefs.capabilities.includes('maintenance')}
                                   onChange={handleCapabilitiesChange
                            }
                            />
                            <strong>Maintenance: Core Stability</strong>
                            <div className={'small'}>Essential updates to WordPress,
                                themes, and plugins. The foundation of a reliable headquarters.</div>
                        </label>
                        <label className={'form-label d-block'}>
                            <input className={'me-3'}
                                   type={'checkbox'}
                                   name={'capabilities'}
                                   value={'security'}
                                   checked={prefs && prefs.capabilities && prefs.capabilities.includes('security')}
                                   onChange={handleCapabilitiesChange
                            }

                            />
                            <strong>Website Security: The Global Shield</strong>
                            <div className={'small'}>Active defense against malware, brute force attacks, and digital
                                infiltrators.</div>
                        </label>
                        <label className={'form-label d-block'}>
                            <input className={'me-3'}
                                   type={'checkbox'}
                                   name={'capabilities'}
                                   value={'performance'}
                                   checked={prefs && prefs.capabilities && prefs.capabilities.includes('performance')}
                                   onChange={handleCapabilitiesChange
                            }

                            />
                            <strong>Performance: Supercharged Velocity</strong>
                            <div className={'small'}>Fine-tuning server settings and caching to ensure your site is
                                lightning-fast for your fans.</div>
                        </label>
                    </div>
                    <div className={'w-50'}>
                        <h4>The Tactical Overview</h4>
                        {renderSummary()}
                        <p className={'text-end text-primary fs-5'}>{calculateEstimate()}*</p>
                        <div className={'d-flex flex-column align-items-end'}>
                            {saved ? <i className={`d-block bi bi-check fs-1 text-success`}/> : null}
                            <input type='submit'
                                   className={`d-block btn btn-lg btn-success`}
                                   value={'Save Preferences'}
                            />
                        </div>
                        <p className={'small text-secondary mt-3'} style={{textAlign: 'right'}}>* Estimate reflects regular
                            maintenance. Set up may add additional time, and routine costs may vary.</p>
                    </div>
                </div>

            </form>
        </div>
    );
};

export default CompanyPreferences